import { FormJourneyItem, JourneySelectOption, LeadForm } from "@plura/shared/types";
import { cn } from "@plura/shared/utils";
import { dynamicQuestionContent, getCurrentResponseScore } from "@plura/shared/lead-forms";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { motion } from "framer-motion";
import AddressScreen from "../AddressScreen";
import ContactInfoScreen from "../ContactInfoScreen";
import DateScreen from "../DateScreen";
import DropdownScreen from "../DropdownScreen";
import EmailScreen from "../EmailScreen";
import FileScreen from "../FileScreen";
import LongTextScreen from "../LongTextScreen";
import MultiChoiceScreen from "../MultiChoiceScreen";
import NPSScreen from "../NPSScreen";
import NumberScreen from "../NumberScreen";
import OpinionScaleScreen from "../OpinionScaleScreen";
import PhoneScreen from "../PhoneScreen";
import RatingScreen from "../RatingScreen";
import SchedulerScreen from "../SchedulerScreen";
import ShortTextScreen from "../ShortTextScreen";
import StatementScreen from "../StatementScreen";
import WebsiteScreen from "../WebsiteScreen";
import "./multi-question-page.css";

interface Props {
  id: string;
  step: FormJourneyItem;
  leadForm: LeadForm;
  editorMode: boolean;
  previewMode: boolean;
  active: boolean;
  currentStepOverride?: string | null;
  requiresGDPRConsent?: string;
  onContentClick?: (fieldName: string, blockId: string) => void;
  hiddenFields?: { field: string; value: string }[];
}

// `useFormContext` is optional here — the type-selector preview renders this
// screen outside the live form provider. Returning safe defaults keeps recall
// references rendering as filler badges instead of throwing. Cast matches the
// shape the score/dynamic-content helpers expect (mirrors what every sibling
// screen does with `form.getValues()`).
const useSafeUserResponses = (): { [key: string]: string | number } => {
  const ctx = useFormContext()
  if (!ctx) return {}
  const { conversationId: _convoId, ...rest } = ctx.getValues()
  return rest as { [key: string]: string | number }
}

// Dispatch a single child to its standalone screen component. We pass through
// the same shape ScreenRendering uses, with handleAnswer intentionally
// undefined so e.g. selecting a multi-choice option doesn't auto-advance the
// whole page — the page has a single Continue button.
const renderChild = (
  child: FormJourneyItem,
  shared: {
    editorMode: boolean;
    previewMode: boolean;
    active: boolean;
    leadForm: LeadForm;
    currentStepOverride: string | null;
    requiresGDPRConsent?: string;
    onContentClick?: (fieldName: string, blockId: string) => void;
    hiddenFields?: { field: string; value: string }[];
  }
) => {
  const commonProps = {
    id: child.blockId,
    editorMode: shared.editorMode,
    previewMode: shared.previewMode,
    currentStepOverride: shared.currentStepOverride,
    leadForm: shared.leadForm,
    active: shared.active,
    requiresGDPRConsent: shared.requiresGDPRConsent,
    hiddenFields: shared.hiddenFields,
    step: {
      ...child,
      selectOptions: child.selectOptions
        ? (child.selectOptions as JourneySelectOption[]).map(o => JourneySelectOption.parse(o))
        : null,
    },
    onContentClick: shared.onContentClick,
  };

  switch (child.type) {
    case 'EMAIL': return <EmailScreen {...commonProps} />;
    case 'STATEMENT': return <StatementScreen {...commonProps} handleAnswer={undefined} />;
    case 'PHONE_NUMBER': return <PhoneScreen {...commonProps} />;
    case 'WEBSITE': return <WebsiteScreen {...commonProps} />;
    case 'NUMBER': return <NumberScreen {...commonProps} />;
    case 'FILE': return <FileScreen {...commonProps} />;
    case 'SHORT_TEXT': return <ShortTextScreen {...commonProps} />;
    case 'LONG_TEXT': return <LongTextScreen {...commonProps} />;
    case 'MULTIPLE_CHOICE': return <MultiChoiceScreen {...commonProps} handleAnswer={undefined} />;
    case 'DROPDOWN': return <DropdownScreen {...commonProps} handleAnswer={undefined} />;
    case 'OPTION_SCALE': return <OpinionScaleScreen {...commonProps} handleAnswer={undefined} />;
    case 'NPS': return <NPSScreen {...commonProps} handleAnswer={undefined} />;
    case 'RATING': return <RatingScreen {...commonProps} handleAnswer={undefined} />;
    case 'CONTACT_INFO': return <ContactInfoScreen {...commonProps} />;
    case 'ADDRESS': return <AddressScreen {...commonProps} />;
    case 'DATE': return <DateScreen {...commonProps} />;
    case 'SCHEDULER':
      return (
        <div className="mqp-scheduler">
          <SchedulerScreen {...commonProps} handleAnswer={undefined} embeddedInline />
        </div>
      );
    default:
      return (
        <div className="text-caption text-t-tertiary italic">
          This question type isn't supported inside a multi-question page yet.
        </div>
      );
  }
};

const MultiQuestionPageScreen = ({
  id,
  step,
  leadForm,
  editorMode,
  previewMode,
  active,
  currentStepOverride,
  requiresGDPRConsent,
  onContentClick,
  hiddenFields,
}: Props) => {
  const allItems = leadForm.journeyItems ?? []
  const userResponses = useSafeUserResponses()
  const currentResponseScore = userResponses
    ? getCurrentResponseScore(userResponses, leadForm)
    : 0

  const children = useMemo(
    () => allItems
      .filter(i => i.parentId === step.id)
      .sort((a, b) => a.order - b.order),
    [allItems, step.id]
  )

  const density = (step.thirdContent === 'compact') ? 'compact' : 'comfortable'
  // Tighter than the engine's between-block spacing because the inline child
  // typography is smaller — the original gap-6/gap-4 looked sparse.
  const gap = density === 'compact' ? 'gap-2.5' : 'gap-4'

  const align = step.contentAlignment ?? 'LEFT'
  const textAlignClass = align === 'CENTER' ? 'text-center' : align === 'RIGHT' ? 'text-right' : 'text-left'

  const handleContentClick = (field: 'content' | 'secondaryContent') => {
    if (editorMode && !previewMode) return
    onContentClick?.(field, id)
  }

  const renderDynamic = (raw: string) => dynamicQuestionContent({
    content: raw,
    score: currentResponseScore,
    editMode: editorMode,
    previewMode: previewMode ?? false,
    hiddenFields,
    journeyItems: leadForm.journeyItems ?? [],
    userResponses,
  })

  // Mirror the per-question screens' `semiFade` behavior: as soon as any
  // child on this page has a value, soften the heading + caption so they
  // recede behind the active answer area. EmailScreen et al. trigger this
  // off `inputRef.current?.value.length`; the MQP equivalent is "any child
  // input answered" since the page hosts multiple inputs.
  const anyChildAnswered = useMemo(
    () => children.some(c => {
      const v = userResponses[c.blockId]
      return v !== undefined && v !== null && v !== ''
    }),
    [children, userResponses]
  )

  const sharedChildProps = {
    editorMode,
    previewMode,
    active,
    leadForm,
    currentStepOverride: currentStepOverride ?? null,
    requiresGDPRConsent,
    onContentClick,
    hiddenFields,
  }

  return (
    <div className="questionInner mqp-screen-root">
      <div className={textAlignClass}>
        <h4
          onClick={() => handleContentClick('content')}
          className={cn(anyChildAnswered && 'semiFade', 'question')}
        >
          <motion.span dangerouslySetInnerHTML={{ __html: renderDynamic(step.content || 'Tell us about yourself') }} />
        </h4>
        {step.secondaryContent && (
          <p
            onClick={() => handleContentClick('secondaryContent')}
            className={cn(anyChildAnswered && 'semiFade', 'caption')}
          >
            <motion.span dangerouslySetInnerHTML={{ __html: renderDynamic(step.secondaryContent) }} />
          </p>
        )}
      </div>

      {children.length > 0 && (
        <div className={cn('answerInputWrapper flex flex-col mt-4 w-full', gap)}>
          {children.map((child, idx) => (
            <div
              key={child.id}
              className="mqp-inline w-full"
              // Used by CSS to append a red asterisk after the question heading
              // for required fields inside an MQP. Children's screen components
              // don't render a required indicator on their own.
              data-required={child.manditory ? 'true' : undefined}
              // Read by `useAutoFocusAfterAnimation` — only the first child of
              // an MQP is allowed to auto-focus on entrance. Without this,
              // every child screen would try to focus its input and the
              // browser would scroll to whichever wins last.
              data-mqp-first-child={idx === 0 ? 'true' : undefined}
            >
              {renderChild(child, sharedChildProps)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MultiQuestionPageScreen
